import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { CustomSpinner } from "@components/Spinner/SpinnerMyAccountView";
import { sendInvoiceByEmail } from "@services/InvoiceServices";
import "./InvoiceActionsModal.scss";

interface InvoiceActionsModalProps {
	show: boolean;
	onHide: () => void;
	invoice: any;
	onCancelInvoice: (invoiceApiId: string, invoiceId: string) => Promise<boolean>;
	onDownloadInvoice: (
		invoiceApiId: string,
		ticketNumber: number,
		matricula: string,
		format: string
	) => void;
}

export const InvoiceActionsModal: React.FC<InvoiceActionsModalProps> = ({
	show,
	onHide,
	invoice,
	onCancelInvoice,
	onDownloadInvoice,
}) => {
	const [loading, setLoading] = useState({
		email: false,
		cancel: false,
		downloadPdf: false,
		downloadXml: false,
	});

	const [emailSent, setEmailSent] = useState(false);

	const handleSendEmail = async () => {
		setLoading((prev) => ({ ...prev, email: true }));
		try {
			// Crear el objeto de API de factura necesario
			const invoiceApiData = {
				id: invoice.ApiId,
				folio_number: invoice.ticketNumber,
				customer: {
					tax_id: invoice.rfc,
				},
			};

			await sendInvoiceByEmail(invoiceApiData, invoice);
			setEmailSent(true);
			setTimeout(() => {
				setEmailSent(false);
				onHide();
			}, 2000);
		} catch (error) {
			console.error("Error al enviar correo:", error);
		} finally {
			setLoading((prev) => ({ ...prev, email: false }));
		}
	};

	const handleCancelInvoice = async () => {
		setLoading((prev) => ({ ...prev, cancel: true }));
		try {
			const result = await onCancelInvoice(invoice.ApiId, invoice.id);
			if (result) {
				setTimeout(() => {
					onHide();
				}, 1000);
			}
		} catch (error) {
			console.error("Error al cancelar factura:", error);
		} finally {
			setLoading((prev) => ({ ...prev, cancel: false }));
		}
	};

	const handleDownload = (format: string) => {
		// Determinar qué estado de carga actualizar según el formato
		const loadingKey = format === "pdf" ? "downloadPdf" : "downloadXml";

		setLoading((prev) => ({ ...prev, [loadingKey]: true }));
		try {
			onDownloadInvoice(invoice.ApiId, invoice.ticketNumber, invoice.matricula, format);
		} catch (error) {
			console.error("Error al descargar:", error);
		} finally {
			setTimeout(() => {
				setLoading((prev) => ({ ...prev, [loadingKey]: false }));
			}, 1000);
		}
	};

	return (
		<Modal className="invoice-actions-modal" show={show} onHide={onHide} centered>
			<div className="invoice-actions-content">
				<Modal.Header closeButton>
					<Modal.Title>Acciones de Factura</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="invoice-info">
						<h6>Información de la Factura</h6>
						<p>
							<strong>Cliente:</strong> {invoice.name}
						</p>
						<p>
							<strong>Monto:</strong> ${invoice.amount} MXN
						</p>
						<p>
							<strong>Ticket:</strong> #{invoice.ticketNumber}
						</p>
						<p>
							<strong>Email:</strong> {invoice.email}
						</p>
					</div>

					<div className="actions-grid">
						<div className="action-card">
							<div className="action-icon email-icon">📧</div>
							<h6>Reenviar por Correo</h6>
							<p>Envía la factura al email del cliente</p>
							<div className="single-button-container">
								<button
									className="action-btn email-btn"
									onClick={handleSendEmail}
									disabled={loading.email || emailSent}
								>
									{loading.email ? (
										<CustomSpinner height="20px" borderSize="2px" size="20px" />
									) : emailSent ? (
										"✓ Enviado"
									) : (
										"Enviar Correo"
									)}
								</button>
							</div>
						</div>

						<div className="action-card">
							<div className="action-icon download-icon">📥</div>
							<h6>Descargar Archivos</h6>
							<p>Descarga PDF o XML de la factura</p>
							<div className="download-buttons">
								<button
									className="action-btn download-btn"
									onClick={() => handleDownload("pdf")}
									disabled={loading.downloadPdf}
								>
									{loading.downloadPdf ? (
										<CustomSpinner height="16px" borderSize="2px" size="16px" />
									) : (
										"PDF"
									)}
								</button>
								<button
									className="action-btn download-btn"
									onClick={() => handleDownload("xml")}
									disabled={loading.downloadXml}
								>
									{loading.downloadXml ? (
										<CustomSpinner height="16px" borderSize="2px" size="16px" />
									) : (
										"XML"
									)}
								</button>
							</div>
						</div>

						<div className="action-card">
							<div className="action-icon cancel-icon">❌</div>
							<h6>Cancelar Factura</h6>
							<p>Cancela permanentemente esta factura</p>
							<div className="single-button-container">
								<button
									className="action-btn cancel-btn"
									onClick={handleCancelInvoice}
									disabled={loading.cancel}
								>
									{loading.cancel ? (
										<CustomSpinner height="20px" borderSize="2px" size="20px" />
									) : (
										"Cancelar Factura"
									)}
								</button>
							</div>
						</div>
					</div>
				</Modal.Body>
			</div>
		</Modal>
	);
};
